import React, { FunctionComponent, useCallback, useMemo, useState } from 'react';
import { StateValues } from 'react-use-form-state';
import {
  AdminApi,
  AdminProductDetail,
  AdminProductInvestmentCreationRequest,
  AdminProductListing,
  AdminSignedDocumentCreationRequest,
  ComplianceSubjectEnum,
  DocumentsApi,
  NetworkNameTechnicalEnum,
} from 'api';
import useApiCall from 'hooks/use-api-call';
import Translate from 'ui/atoms/translate';
import { Modal, ModalHeader, ModalContent, ModalFooter, ModalFooterButtons, makeModalForm } from 'ui/molecules/modal';
import { toMoney } from 'src/ui/helper/money';
import useIsMedia, { DEVICES } from 'ui/hooks/use-is-media';
import Tooltip, { TooltipPosition } from 'src/ui/atoms/tooltip';
import * as Styled from './styled';
import Icon from 'src/ui/atoms/icon';
import { compact, toNumber } from 'lodash';
import Currency from 'src/ui/atoms/currency';
import FormGroup, { makeRequirement } from 'src/ui/molecules/form-group';
import NumberAtom from 'ui/atoms/number';
import { makeBigNumber } from 'src/ui/helper/big-number';
import Grid, { Col } from 'src/ui/atoms/grid';
import { FileToUpload, FileWithDocType } from 'src/ui/atoms/file-upload';
import { snakeToCamelCaseWithNumbers } from 'src/ui/helper/case-transforms';
import useTranslate, { useTranslateWithStringArgs } from 'src/ui/hooks/use-translate';
import { useMultipleSingleFileUploads } from 'src/ui/atoms/file-upload/hooks/use-multiple-single-file-uploads';
import Spacer from 'src/ui/atoms/spacer';
import Hint from 'src/ui/atoms/hint';
import Button from 'src/ui/atoms/button';
import { handleError } from 'src/ui/helper/error-handling';

interface AddInvesmentModalProps {
  products: AdminProductListing[];
  onAddNewInvestment: () => void;
  onClose: () => void;
  onSearchProduct: (val: string) => void;
}

interface AddInvesmentFields {
  product: string;
  units: number;
  pricePerUnit: string;
  walletAddress: string;
  agio?: string;
  disagio?: string;
  signingDate: Date;
  signedDocuments: FileToUpload[];
}

export const Form = makeModalForm<AddInvesmentFields>();

const AddInvesmentModal: FunctionComponent<AddInvesmentModalProps> = ({
  products,
  onAddNewInvestment,
  onClose,
  onSearchProduct,
}) => {
  const [selectedProduct, setSelectedProduct] = useState<AdminProductDetail | undefined>(undefined);
  const [validationError, setValidationError] = useState('');

  const isPhone = useIsMedia(DEVICES.phone);

  const { makeAuthenticatedApi, withApi, loading: apiLoading, error } = useApiCall();
  const api: AdminApi = useMemo(() => makeAuthenticatedApi(AdminApi), [makeAuthenticatedApi]);
  const documentsApi: DocumentsApi = useMemo(() => makeAuthenticatedApi(DocumentsApi), [makeAuthenticatedApi]);

  const translate = useTranslateWithStringArgs();

  const {
    documentsUploaded,
    onChangeDocument,
    addMoreDocuments,
    error: fileUploadError,
  } = useMultipleSingleFileUploads();

  const uploadFiles = useCallback(async (documents: FileWithDocType[]) => {
    let documentsRequest: Array<AdminSignedDocumentCreationRequest> = [];

    await Promise.all(
      documents.map(async (document: FileWithDocType) => {
        if (!document.documentType || !document.file) {
          documentsRequest = [];
          return documentsRequest;
        }

        const documentResponse = await documentsApi.adminDocumentsCreate({ file: document.file });
        documentsRequest.push({
          documentId: documentResponse.id,
          complianceSubject: document.documentType as ComplianceSubjectEnum,
        });
      }),
    );

    return documentsRequest;
  }, []);

  const validate = useCallback((documents: FileWithDocType[]) => {
    let errorMessage = '';

    if (documents.some((document) => !document.documentType)) {
      errorMessage = translate('addInvestment.errors.noDocumentType');
      return errorMessage;
    }

    const checkedDocumentTypes = new Set();

    const documentTypesToSubmit = [...documents.map((doc) => doc.documentType)];

    documentTypesToSubmit.some((documentType) => {
      if (checkedDocumentTypes.has(documentType)) {
        errorMessage = translate('addInvestmentDocuments.errors.multipleDocsForDocType');
        return true;
      }

      checkedDocumentTypes.add(documentType);
      return false;
    });

    return errorMessage;
  }, []);

  const onSubmit = useCallback(
    async (fieldsValues: StateValues<AddInvesmentFields>) => {
      setValidationError('');

      const documents = documentsUploaded.filter((doc) => doc.document.file).map((doc) => doc.document);

      const fieldsValidation = validate(documents);
      if (fieldsValidation.length > 0) {
        setValidationError(fieldsValidation);
        return;
      }

      const params: AdminProductInvestmentCreationRequest = {
        productId: fieldsValues.product,
        units: toNumber(fieldsValues.units),
        pricePerUnit: toMoney(fieldsValues.pricePerUnit),
        walletAddress: fieldsValues.walletAddress,
        signedAt: fieldsValues.signingDate,
      };

      if (fieldsValues.disagio) {
        params.disagio = toMoney(fieldsValues.disagio);
      }
      if (fieldsValues.agio) {
        params.agioPercent = fieldsValues.agio;
      }
      params.signedDocuments = await uploadFiles(documents);

      withApi(async () => {
        await api.adminInvestmentsCreate({ adminProductInvestmentCreationRequest: params });
        onAddNewInvestment();
      });
    },
    [[JSON.stringify(documentsUploaded)]],
  );

  const getSelectedProductDetails = async (productId: string) => {
    const product = await api.adminProductsRetrieve({
      id: productId,
    });
    setSelectedProduct(product);
  };

  const isAmountDivisibleByStep = useCallback(
    (units: string, stepSize: number) => {
      if (!selectedProduct) return true;

      return !!units && makeBigNumber(units).mod(stepSize).eq(0);
    },
    [JSON.stringify(selectedProduct)],
  );

  const isStellarNetwork = useCallback((network?: NetworkNameTechnicalEnum | null | undefined): boolean => {
    if (!network) return false;

    return network.includes('stellar');
  }, []);

  const getDocumentTypes = useCallback(() => {
    return Object.values(ComplianceSubjectEnum).map((documentType) => {
      return {
        label: translate(`documentTypes.${snakeToCamelCaseWithNumbers(documentType)}`),
        value: documentType,
      };
    });
  }, []);

  const { getErrorForField: getWalletError } = handleError({
    error,
    translate: useTranslate(),
    specialCodes: {
      wallet_address_unknown: {
        field: 'wallet_address',
        translateKey: 'addInvestment.errors.accountNotRegistered',
      },
      investor_has_no_kyc: {
        field: 'wallet_address',
        translateKey: 'addInvestment.errors.noKyc',
      },
      product_not_listed_for_distribution_platform: {
        field: 'wallet_address',
        translateKey: 'addInvestment.errors.accountNotRegistered',
      },
      product_wallet_assignment_missing: {
        field: 'wallet_address',
        translateKey: 'addInvestment.errors.productWalletAssignmentMissingError2',
      },
    },
  });

  return (
    <Modal onClose={onClose}>
      <Form
        i18nKey="addInvestment"
        onSubmit={(values: StateValues<AddInvesmentFields>) => {
          onSubmit(values);
        }}
        error={error}
      >
        <ModalHeader>
          <Translate name="addInvestment.title" />
        </ModalHeader>
        <ModalContent>
          <Form.ValueProvider>
            {({ units, pricePerUnit }) => (
              <>
                <Form.Group name="product" required={true} occupyTooltipSpace={true}>
                  <Form.Select
                    options={products.map((product) => ({
                      value: product.id,
                      label: product.name,
                    }))}
                    onInputChange={onSearchProduct}
                    onChange={(productId: string) => {
                      const foundProduct = products.find((product) => product.id === productId);
                      if (foundProduct) {
                        getSelectedProductDetails(foundProduct.id);
                      }
                    }}
                  />
                </Form.Group>
                <Form.Group
                  name="units"
                  required={true}
                  tooltip={
                    <Tooltip
                      content={<Translate name="addInvestment.fields.units.tooltip" />}
                      position={isPhone ? TooltipPosition.BOTTOM : TooltipPosition.RIGHT}
                    >
                      <Icon name="question" circular={true} size="large" padded="none" color="info" />
                    </Tooltip>
                  }
                  requirements={compact([
                    selectedProduct &&
                      selectedProduct.stepSize &&
                      makeRequirement(
                        <Translate
                          name="addInvestment.fields.units.requirements.divisibleByStepSize"
                          args={[<NumberAtom key={0}>{selectedProduct.stepSize}</NumberAtom>]}
                        />,
                        isAmountDivisibleByStep(units, selectedProduct.stepSize),
                        true,
                      ),
                    selectedProduct &&
                      selectedProduct.stepSize &&
                      makeRequirement(
                        <Translate
                          name="addInvestment.fields.units.requirements.min"
                          args={[
                            <NumberAtom key={0} type="shares">
                              {selectedProduct.stepSize}
                            </NumberAtom>,
                          ]}
                        />,
                        parseInt(units) >= selectedProduct.stepSize,
                        true,
                      ),
                    selectedProduct &&
                      selectedProduct.remainingAmount &&
                      makeRequirement(
                        <Translate
                          name="addInvestment.fields.units.requirements.max"
                          args={[
                            <NumberAtom key={0} type="shares">
                              {selectedProduct.remainingAmount}
                            </NumberAtom>,
                          ]}
                        />,
                        parseInt(units) <= selectedProduct.remainingAmount,
                        true,
                      ),
                  ])}
                >
                  <Form.Input type="number" min={0} max={selectedProduct?.remainingAmount} />
                  {Form.Validators.Range(0, selectedProduct?.remainingAmount)}
                  {selectedProduct?.stepSize &&
                    Form.Validators.Divisible(
                      selectedProduct?.stepSize,
                      <Translate
                        name="addInvestment.fields.units.requirements.divisibleByStepSize"
                        args={[selectedProduct?.stepSize]}
                      />,
                    )}
                </Form.Group>
                <Form.Group
                  name="pricePerUnit"
                  required={true}
                  occupyTooltipSpace={true}
                  info={
                    <Translate
                      name="addInvestment.fields.pricePerUnit.info"
                      args={[<Currency>{toMoney(parseInt(units) * toNumber(pricePerUnit))}</Currency>]}
                    />
                  }
                >
                  <Form.Input type="number" min={0} isCurrency />
                </Form.Group>
                <Form.Group
                  name="walletAddress"
                  required={true}
                  fieldError={getWalletError('wallet_address')}
                  tooltip={
                    <Tooltip
                      content={<Translate name="addInvestment.fields.walletAddress.tooltip" />}
                      position={isPhone ? TooltipPosition.BOTTOM : TooltipPosition.RIGHT}
                    >
                      <Icon name="question" circular={true} size="large" padded="none" color="info" />
                    </Tooltip>
                  }
                >
                  <Form.Input />
                  {selectedProduct && isStellarNetwork(selectedProduct?.network) && Form.Validators.StellarAddress()}
                  {selectedProduct && !isStellarNetwork(selectedProduct?.network) && Form.Validators.EthereumAddress()}
                  {!isStellarNetwork(selectedProduct?.network) && Form.Validators.TokenAddressChecksum()}
                </Form.Group>
                <Styled.RightAlignedElement spacing="regular">
                  <Grid>
                    <Col leftCol width={6} tablet={12} phone={12}>
                      <Form.Group name="disagio" spacing={isPhone ? 'medium' : 'none'}>
                        <Form.Input isCurrency />
                      </Form.Group>
                    </Col>
                    <Col rightCol width={6} tablet={12} phone={12}>
                      <Form.Group name="agio" spacing={isPhone ? 'medium' : 'none'}>
                        <Form.Input isPercentage />
                      </Form.Group>
                    </Col>
                  </Grid>
                </Styled.RightAlignedElement>
                <Styled.RightAlignedElement spacing="regular">
                  <Form.Group name="signingDate" required={true}>
                    <Form.DateInput hasTime={true} />
                    {Form.Validators.PastDate(0, false, <Translate name="addInvestment.errors.signingDatePast" />)}
                  </Form.Group>
                </Styled.RightAlignedElement>
                <Form.Group name="signedDocuments" required={true}>
                  {documentsUploaded &&
                    documentsUploaded.map((documentUploaded, index) => (
                      <>
                        <Form.FileUpload
                          key={documentUploaded.index}
                          onChange={(document: FileWithDocType) => {
                            setValidationError('');
                            onChangeDocument(document, documentUploaded.index);
                          }}
                          documentTypes={getDocumentTypes()}
                        />
                        {index !== documentsUploaded.length - 1 && <Spacer y={4} />}
                      </>
                    ))}
                </Form.Group>
                {(validationError || fileUploadError) && (
                  <Hint variant="danger">
                    <Translate name={validationError || fileUploadError} />
                  </Hint>
                )}
                <Button variant="link" icon="plus" size="small" inline iconSize="small" onClick={addMoreDocuments}>
                  <Translate name="addInvestment.addMoreDocuments" />
                </Button>
              </>
            )}
          </Form.ValueProvider>
          <Form.GenericErrorMessages />
        </ModalContent>
        <ModalFooter>
          <ModalFooterButtons
            actionButtons={[
              {
                name: 'cancel',
                content: <Translate name="common.cancel" />,
                size: 'large',
                onClick: () => onClose(),
              },
              {
                name: 'send',
                content: <Translate name="common.add" />,
                variant: 'primary',
                loading: apiLoading,
                type: 'submit',
                size: 'large',
              },
            ]}
          />
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default AddInvesmentModal;
